import { AppShellSkeleton, AuthPageSkeleton } from '@/components/core';
import { isPublicRoute } from '@/constant/auth-public-routes';
import {
  APP_PATHS,
  MY_REVIEWS_TABS,
  PROJECTS_TABS,
  SERVICE_REQUEST_TYPES,
} from '@/constant/app-paths';
import { SidebarProvider } from '@/components/ui-kit/sidebar';
import { Toaster } from '@/components/ui-kit/toaster';
import {
  HRM_MANAGE_PERMISSIONS,
  SERVICE_REQUEST_PAGE_PERMISSIONS,
} from '@/constant/hr/manage-permissions';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useLanguageContext } from '@/i18n/language-context';
import { MainLayout } from '@/layout/main-layout/main-layout';
import {
  ManageAttendancePage,
  MyAttendancePage,
  MyTimesheetPage,
  TimesheetManagerPage,
  UserAttendanceHistoryPage,
  UserTimesheetDetailPage,
} from '@/modules/time-attendance';
import { NotFoundPage, ServiceUnavailablePage } from '@/modules/error-view';
import { EmployeeDetailsPage, EmployeeFormPage, EmployeeListPage } from '@/modules/hr-directory';
import { LogsPage } from '@/modules/settings/system/audit-log';
import { HomePage } from '@/modules/home';
import {
  LeaveCalendarPage,
  MyTimeOffPage,
  SickLeavePage,
  TeamTimeOffPage,
} from '@/modules/time-off';
import { ManageLunchPage, MealMenuPage, RequestLunchPage } from '@/modules/lunch';
import {
  CtcLevelManagerPage,
  DepartmentManagerPage,
  DesignationManagerPage,
  FormEditorPage,
  FormListPage,
  HolidayPage,
  AttendanceRulesPage,
  KpiConfigurationPage,
  LeaveBuilderPage,
  LocationManagerPage,
  NocTypeManagerPage,
  ProjectsPage,
  RoleManagerPage,
  SettingsLayout,
  SettingsOverviewPage,
  SettingsSoonPage,
  SkillManagerPage,
  SurveyFormEditorPage,
  SurveyFormListPage,
} from '@/modules/settings';
import {
  MyReviewsPage,
  ReviewDashboardPage,
  ReviewDetailsPage,
  ReviewsListPage,
  SubmissionAnswerPage,
} from '@/modules/performance';
import { ProfileCompletionGate, ProfileEditPage, ProfilePage } from '@/modules/profile';
import { SETTINGS_ACCESS_PERMISSIONS, SOON_SETTINGS_PAGES } from '@/constant/settings-menu';
import {
  OfferBuilderPage,
  OfferListPage,
  ProfileBuilderPage,
  ProfileListPage,
} from '@/modules/hiring';
import {
  ByodApplyPage,
  IdCardApplyPage,
  MetlifeApplyPage,
  NocApplyPage,
  NocReportPage,
  ServiceRequestsPage,
} from '@/modules/service-requests';
import {
  BatchDetailsPage,
  ResponseViewPage,
  SurveyListPage,
  SubmissionAnswerPage as SurveySubmissionAnswerPage,
  SubmissionsListPage as SurveySubmissionsListPage,
} from '@/modules/surveys';
import { ClientMiddleware } from '@/state/client-middleware';
import { OrganizationTimeZoneGate } from '@/state/organization-time-zone-gate';
import { Guard } from '@/state/store/auth/guard';
import { ProtectedRoute } from '@/state/store/auth/protected-route';
import { ThemeProvider } from '@/styles/theme/theme-provider';
import { useTrackInAppHistory } from '@/hooks/use-in-app-history';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthRoutes } from './auth.route';
import { legacyRedirectRoutes } from './legacy-redirects';

const P = APP_PATHS;
const SR = APP_PATHS.SERVICE_REQUESTS;
const { NOC, BYOD, ID_CARD, INSURANCE } = SERVICE_REQUEST_TYPES;
const PERM = HRM_PERMISSIONS;
/** Settings pages open with their own page permission (Phase 8); the editors keep the write ones. */
const S = HRM_PERMISSIONS.SETTINGS;
const TA = HRM_PERMISSIONS.TIME_ATTENDANCE;
const TO = HRM_PERMISSIONS.TIME_OFF;
const MANAGE = HRM_MANAGE_PERMISSIONS;

/** `/people/new` adds a person (create permission); `/people/:employeeId/edit` edits one (update permission). */
const EmployeeFormRoute = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const permission = employeeId ? PERM.PEOPLE.UPDATE : PERM.PEOPLE.CREATE;

  return (
    <ProtectedRoute key={permission} permissions={[permission]}>
      <EmployeeFormPage />
    </ProtectedRoute>
  );
};

/** Records every navigation for the top bar's Back button. Sits beside the routes, outside any layout,
 * so pages and old-URL redirects (which leave the layout for a moment) are all seen. */
const InAppHistoryTracker = () => {
  useTrackInAppHistory();
  return null;
};

/** One page behind `ProtectedRoute`: it opens for whoever holds any of `permissions`. */
const page = (path: string, permissions: string[], element: React.ReactNode) => (
  <Route
    key={path}
    path={path}
    element={<ProtectedRoute permissions={permissions}>{element}</ProtectedRoute>}
  />
);

export const AppRoutes = () => {
  const { isInitialLoading } = useLanguageContext();

  // The first translations are loading: show the frame of the page being opened (sign-in or app).
  if (isInitialLoading) {
    const isAuthPage = isPublicRoute(window.location.pathname);
    return isAuthPage ? <AuthPageSkeleton /> : <AppShellSkeleton />;
  }
  return (
    <div className="min-h-dvh bg-background font-sans antialiased relative">
      <ClientMiddleware>
        <ThemeProvider>
          <SidebarProvider>
            <InAppHistoryTracker />
            <Routes>
              {AuthRoutes}
              <Route
                element={
                  <Guard>
                    <OrganizationTimeZoneGate>
                      <ProfileCompletionGate>
                        <MainLayout />
                      </ProfileCompletionGate>
                    </OrganizationTimeZoneGate>
                  </Guard>
                }
              >
                <Route path={P.MY_PROFILE} element={<ProfilePage />} />
                <Route path={P.MY_PROFILE_EDIT} element={<ProfileEditPage />} />

                {/* People */}
                {page(P.HOME, [PERM.HOME.VIEW], <HomePage />)}
                {page(P.PEOPLE, [PERM.PEOPLE.VIEW], <EmployeeListPage />)}
                <Route path={P.PEOPLE_NEW} element={<EmployeeFormRoute />} />
                {page(`${P.PEOPLE}/:employeeId`, [PERM.PEOPLE.VIEW], <EmployeeDetailsPage />)}
                <Route path={`${P.PEOPLE}/:employeeId/edit`} element={<EmployeeFormRoute />} />

                {/* Time & Attendance */}
                {page(P.TIME_TRACKER, [TA.VIEW_TIME_TRACKER], <MyTimesheetPage />)}
                {page(P.ATTENDANCE, [TA.VIEW_ATTENDANCE], <MyAttendancePage />)}
                {/* The tabs inside follow the Attendance and Overtime permissions. */}
                {page(P.TEAM_ATTENDANCE, [TA.VIEW_TEAM_ATTENDANCE], <ManageAttendancePage />)}
                {page(
                  `${P.TEAM_ATTENDANCE}/:userId`,
                  [TA.VIEW_TEAM_ATTENDANCE],
                  <UserAttendanceHistoryPage />
                )}
                {page(P.TEAM_TIMESHEETS, [TA.VIEW_TEAM_TIMESHEETS], <TimesheetManagerPage />)}
                {page(
                  `${P.TEAM_TIMESHEETS}/:userId`,
                  [TA.VIEW_TEAM_TIMESHEETS],
                  <UserTimesheetDetailPage />
                )}

                {/* Time Off */}
                {/* The tabs inside follow the Leave and TOIL permissions. */}
                {page(P.MY_TIME_OFF, [TO.VIEW_OWN], <MyTimeOffPage />)}
                {page(P.TEAM_TIME_OFF, [TO.VIEW_TEAM], <TeamTimeOffPage />)}
                {page(P.TIME_OFF_CALENDAR, [TO.VIEW_CALENDAR], <LeaveCalendarPage />)}
                {page(P.SICK_LEAVE, [TO.VIEW_SICK_LEAVE], <SickLeavePage />)}

                {/* Performance */}
                {page(P.MY_REVIEWS, [PERM.PERFORMANCE.VIEW_MY_REVIEWS], <MyReviewsPage />)}
                {/* The submissions list is now the Submissions tab of My Reviews. */}
                <Route
                  path={P.MY_REVIEW_SUBMISSIONS}
                  element={
                    <Navigate to={`${P.MY_REVIEWS}?tab=${MY_REVIEWS_TABS.SUBMISSIONS}`} replace />
                  }
                />
                {page(
                  `${P.MY_REVIEW_SUBMISSIONS}/:pmReviewDetailId`,
                  [PERM.PERFORMANCE.SUBMISSIONS_VIEW_OWN],
                  <SubmissionAnswerPage />
                )}
                {page(P.ALL_REVIEWS, [PERM.PERFORMANCE.REVIEWS_VIEW], <ReviewsListPage />)}
                {page(
                  `${P.ALL_REVIEWS}/:reviewId`,
                  [PERM.PERFORMANCE.REVIEWS_VIEW],
                  <ReviewDetailsPage />
                )}
                {page(
                  `${P.ALL_REVIEWS}/:reviewId/dashboard`,
                  [PERM.PERFORMANCE.DASHBOARD_VIEW],
                  <ReviewDashboardPage />
                )}
                {/* The dashboard opens from a review's details now (Phase 6). */}
                <Route
                  path={P.PERFORMANCE_DASHBOARD}
                  element={<Navigate to={P.ALL_REVIEWS} replace />}
                />

                {/* Hiring */}
                {page(P.CANDIDATES, [PERM.HIRING.CANDIDATES_VIEW], <ProfileListPage />)}
                {page(P.CANDIDATES_NEW, [PERM.HIRING.CANDIDATES_CREATE], <ProfileBuilderPage />)}
                {page(P.OFFERS, [PERM.HIRING.OFFERS_VIEW], <OfferListPage />)}
                {page(P.OFFERS_NEW, [PERM.HIRING.OFFERS_CREATE], <OfferBuilderPage />)}

                {/* Surveys */}
                {page(P.ALL_SURVEYS, [PERM.SURVEYS.VIEW], <SurveyListPage />)}
                {page(`${P.ALL_SURVEYS}/:batchId`, [PERM.SURVEYS.VIEW], <BatchDetailsPage />)}
                {page(
                  `${P.ALL_SURVEYS}/:batchId/:submissionId`,
                  [PERM.SURVEYS.VIEW_RESPONSE],
                  <ResponseViewPage />
                )}
                {page(
                  P.MY_SURVEY_RESPONSES,
                  [PERM.SURVEYS.RESPONSES_VIEW_OWN],
                  <SurveySubmissionsListPage />
                )}
                {page(
                  `${P.MY_SURVEY_RESPONSES}/:submissionId`,
                  [PERM.SURVEYS.RESPONSES_VIEW_OWN],
                  <SurveySubmissionAnswerPage />
                )}

                {/* Meal */}
                {page(P.ORDER_MEAL, [PERM.MEAL.LUNCH_VIEW_OWN], <RequestLunchPage />)}
                {page(P.MEAL_ORDERS, [PERM.MEAL.LUNCH_VIEW_ALL], <ManageLunchPage />)}

                {/* Service Requests: one page; each type keeps its apply page (and NOC its letter). */}
                {page(SR, SERVICE_REQUEST_PAGE_PERMISSIONS, <ServiceRequestsPage />)}
                {page(`${SR}/${NOC}/new`, [PERM.SERVICE_REQUEST.NOC_APPLY], <NocApplyPage />)}
                {page(
                  `${SR}/${NOC}/:id/report`,
                  [PERM.SERVICE_REQUEST.NOC_GENERATE],
                  <NocReportPage />
                )}
                {page(`${SR}/${BYOD}/new`, [PERM.SERVICE_REQUEST.BYOD_APPLY], <ByodApplyPage />)}
                {page(
                  `${SR}/${ID_CARD}/new`,
                  [PERM.SERVICE_REQUEST.ID_CARD_APPLY],
                  <IdCardApplyPage />
                )}
                {/* Reviewers (update-status) open the form to edit someone's application. */}
                {page(
                  `${SR}/${INSURANCE}/new`,
                  [
                    PERM.SERVICE_REQUEST.INSURANCE_APPLY,
                    PERM.SERVICE_REQUEST.INSURANCE_UPDATE_STATUS,
                  ],
                  <MetlifeApplyPage />
                )}

                {/* Settings: every page inside the Settings area (its pane beside the page). */}
                <Route path={P.SETTINGS} element={<SettingsLayout />}>
                  <Route
                    index
                    element={
                      <ProtectedRoute permissions={SETTINGS_ACCESS_PERMISSIONS}>
                        <SettingsOverviewPage />
                      </ProtectedRoute>
                    }
                  />
                  {page(P.DEPARTMENTS, [S.VIEW_DEPARTMENTS], <DepartmentManagerPage />)}
                  {page(P.DESIGNATIONS, [S.VIEW_DESIGNATIONS], <DesignationManagerPage />)}
                  {page(P.LOCATIONS, [S.VIEW_LOCATIONS], <LocationManagerPage />)}
                  {page(
                    P.PROJECTS,
                    [S.VIEW_PROJECTS],
                    <ProjectsPage
                      kosovoPermissions={[S.VIEW_PROJECTS]}
                      mbmPermissions={[S.VIEW_PROJECTS]}
                    />
                  )}
                  {/* MBM Projects is a tab of Projects since Phase 8. */}
                  <Route
                    path={P.MBM_PROJECTS}
                    element={<Navigate to={`${P.PROJECTS}?tab=${PROJECTS_TABS.MBM}`} replace />}
                  />
                  {page(P.LEAVE_POLICIES, [S.VIEW_LEAVE_POLICIES], <LeaveBuilderPage />)}
                  {page(P.HOLIDAYS, [S.VIEW_HOLIDAYS], <HolidayPage />)}
                  {page(P.ATTENDANCE_RULES, [S.VIEW_ATTENDANCE_RULES], <AttendanceRulesPage />)}
                  {page(P.MEAL_MENU, [PERM.MEAL.LUNCH_VIEW_ALL], <MealMenuPage />)}
                  {page(P.REVIEW_TEMPLATES, [S.VIEW_REVIEW_TEMPLATES], <FormListPage />)}
                  {page(
                    `${P.REVIEW_TEMPLATES}/:formId`,
                    MANAGE.PERFORMANCE_FORM,
                    <FormEditorPage />
                  )}
                  {page(P.KPIS, [PERM.SETTINGS.KPIS_CONFIGURE], <KpiConfigurationPage />)}
                  {page(P.SKILLS, [S.VIEW_SKILLS], <SkillManagerPage />)}
                  {page(P.SURVEY_FORMS, [S.VIEW_SURVEY_FORMS], <SurveyFormListPage />)}
                  {page(`${P.SURVEY_FORMS}/:formId`, MANAGE.SURVEY_FORM, <SurveyFormEditorPage />)}
                  {page(P.CTC_LEVELS, [S.VIEW_CTC_LEVELS], <CtcLevelManagerPage />)}
                  {page(P.NOC_TYPES, [S.VIEW_NOC_TYPES], <NocTypeManagerPage />)}
                  {page(P.ROLES, [S.VIEW_ROLES], <RoleManagerPage />)}
                  {page(P.AUDIT_LOG, [PERM.SETTINGS.AUDIT_LOG_VIEW], <LogsPage />)}
                  {/* Not built yet: open to everyone who can open Settings at all. */}
                  {SOON_SETTINGS_PAGES.map((item) =>
                    page(
                      item.path,
                      SETTINGS_ACCESS_PERMISSIONS,
                      <SettingsSoonPage name={item.name} />
                    )
                  )}
                </Route>

                <Route path="/503" element={<ServiceUnavailablePage />} />
                <Route path="/404" element={<NotFoundPage />} />
              </Route>

              {/* Old URLs (notifications, e-mails, bookmarks) open the page in its new place. */}
              {legacyRedirectRoutes}

              {/* `/` opens People; a user without access to it is sent on to their first permitted page. */}
              <Route path="/" element={<Navigate to={P.HOME} replace />} />
              <Route path="/dashboard" element={<Navigate to={P.HOME} replace />} />

              <Route path="*" element={<Navigate to="/404" />} />
            </Routes>
          </SidebarProvider>
        </ThemeProvider>
      </ClientMiddleware>
      <Toaster />
    </div>
  );
};
